import { Injectable } from '@angular/core';
import {Observable, Subscription, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';

import {
  CONTENT_TYPE_JSON_UTF8, STAR_FILTER,
  TOTAL_BOOKMARK_INFO_API_URL,
  TOTAL_LINK_INFO_API_URL,
  TOTAL_SEARCH_API_URL
} from '../../defines/protocol';
import {ApiService} from '../api/api.service';
import {SimpleWord} from '../../models/simple-word.model';
import {Word} from "../../models/word.model";
import {Video} from '../../models/video.model';
import {SimpleDialect} from '../../models/dialect.model';
import {Proverb} from '../../models/proverb.model';
import {Saseung3} from '../../models/saseung3.model';
import {Example} from '../../models/example.model';
import {NormalizeSearchKeyword} from '../../defines/search-keyword.func';


export type TotalSearch = {
  countWord?: number,
  listWord?: Array<SimpleWord>,
  countExample?: number,
  listExample?: Array<Example>,
  countVideo?: number,
  listVideo?: Array<Video>,
  countDialect?: number,
  listDialect?: Array<SimpleDialect>,
  countProverb?: number,
  listProverb?: Array<Proverb>,
  countSaseung?: number,
  listSaseung?: Array<Saseung3>,
};

export type BookmarkInfo = {
  numWordBookmark?: number;
  numSajaseungeoBookmark?: number;
  numSaseungBookmark?: number;
  numVideoBookmark?: number;
  numDialectBookmark?: number;
  numProverbBookmark?: number;
};

export type TotalLinkParam = {
  wordIds?: Array<string>,
  dialectIds?: Array<string>
};

export type TotalLinkInfo = {
  word?: Array<Word>,
  dialect?: Array<SimpleDialect>,
};


@Injectable({
  providedIn: 'root'
})
export class TotalService {
  constructor(
    private api: ApiService,
  ) { }


  public GetTotalSearch(keyword: string, level?: STAR_FILTER): Observable<TotalSearch> {
    // console.warn('TotalService::GetTotalSearch()', keyword, level);

    const searchKeyword = NormalizeSearchKeyword(keyword);
    const url = TOTAL_SEARCH_API_URL + searchKeyword + '/'
      + (level ? ('?level=' + level) : ''); // 단어 난이도 필터. level === 0 이면 전체이므로 필터링 하지 않음.
    return this.api.Get(url).pipe(
      map(data => {
        console.log('TotalService::GetTotalSearch()', url, data);

        const totalSearch: TotalSearch = {};
        totalSearch.countWord = data.word_count ?? null;
        if ('word_list' in data && data.word_list) {
          totalSearch.listWord = new Array<SimpleWord>();
          data.word_list.forEach(value => totalSearch.listWord.push(new SimpleWord(value)));
        }

        totalSearch.countExample = data.example_count ?? null;
        if ('example_list' in data && data.example_list) {
          totalSearch.listExample = new Array<Example>();
          data.example_list.forEach(value => totalSearch.listExample.push(new Example(value)));
        }

        totalSearch.countVideo = data.video_count ?? null;
        if ('video_list' in data && data.video_list) {
          totalSearch.listVideo = new Array<Video>();
          data.video_list.forEach(value => totalSearch.listVideo.push(new Video(value)));
        }

        totalSearch.countDialect = data.dialect_count ?? null;
        if ('dialect_list' in data && data.dialect_list) {
          totalSearch.listDialect = new Array<SimpleDialect>();
          data.dialect_list.forEach(value => totalSearch.listDialect.push(new SimpleDialect(value)));
        }

        totalSearch.countProverb = data.proverb_count ?? null;
        if ('proverb_list' in data && data.proverb_list) {
          totalSearch.listProverb = new Array<Proverb>();
          data.proverb_list.forEach(value => totalSearch.listProverb.push(new Proverb(value)));
        }

        totalSearch.countSaseung = data.saseung_count ?? null;
        if ('saseung_list' in data && data.saseung_list) {
          totalSearch.listSaseung = new Array<Saseung3>();
          data.saseung_list.forEach(value => totalSearch.listSaseung.push(new Saseung3(value)));
        }
        return totalSearch;
      })
    );
  }


  /**
   * 각종 책갈피 정보를 받아오는 함수
   */
  public GetBookmarkInfo(): Observable<BookmarkInfo> {
    return this.api.Get(TOTAL_BOOKMARK_INFO_API_URL).pipe(
      map(data => {
        console.log('Total::Bookmark', data);
        const bmInfo: BookmarkInfo = {};
        if ('num_word_bookmark' in data) {
          bmInfo.numWordBookmark = data.num_word_bookmark;
          bmInfo.numSajaseungeoBookmark = data.num_sajaseungeo_bookmark;
          bmInfo.numSaseungBookmark = data.num_saseung_bookmark;
          bmInfo.numVideoBookmark = data.num_video_bookmark;
          bmInfo.numDialectBookmark = data.num_dialect_bookmark;
          bmInfo.numProverbBookmark = data.num_proverb_bookmark;
        }
        return bmInfo;
      }),
      // catchError((err, caught) => {
      //   console.error('sendBookmark', err);
      //   throwError(err);
      //   return caught;
      // })
    );
  }

  /**
   * 단어와 사투리의 링크 정보를 받아오는 함수
   *
   * @param linkParam
   * @constructor
   */
  public GetLinkInfo(linkParam: TotalLinkParam): Observable<TotalLinkInfo> {
    if ((linkParam?.wordIds?.length ?? 0) === 0 && (linkParam?.dialectIds?.length ?? 0) === 0) {
      return throwError(new Error('Both wordIds and dialectIds are empty or not provided.'));
    }
    return this.api.Post(TOTAL_LINK_INFO_API_URL, {
      ...(linkParam?.wordIds ? {word : linkParam.wordIds} : {}),
      ...(linkParam?.dialectIds ? {dialect : linkParam.dialectIds} : {}),
    }, null, CONTENT_TYPE_JSON_UTF8).pipe(
      map(data => {
        console.log('Total::Link', data);

        const linkInfo: TotalLinkInfo = {
          ...(data?.word ? {word: data.word.map((value: any) => new Word(value))} : {}),
          ...(data?.dialect ? {dialect: data.dialect.map((value: any) => new SimpleDialect(value))} : {}),
        };

        return linkInfo;
      })
    );
  }
}
